import { Box, Button, Grid, MenuItem, Select, SelectChangeEvent, TextField, Typography, Modal } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector, } from "../../store/hooks";

import { fetchBankById, addBank, updateBank } from '../../store/bankStore/bankThunk';
import { fetchCurrencies } from '../../store/currencyStore/currencyThunk';
import { fetchCountries } from '../../store/countryStore/countryThunk';
import { IOptions } from '../FormFields/CustomSelect';
import { FormProvider, useForm } from 'react-hook-form';
import { IBankFormInputs } from '../../utils/bank.types';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { ApiStatus } from '../../utils/api.types';
import LoadingButton from '@mui/lab/LoadingButton';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  transition: '0ms',
  width: 650,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  padding: 0
};

interface DialogBankFormProps {
  title?: string;
  bankId?: string;
  open: boolean;
  onClose: (k: any) => void;
}

const DialogBankForm: FC<DialogBankFormProps> = ({ title, onClose, bankId, open }) => {

  const dispatch = useAppDispatch();
  const params = useParams();
  const [countryOptions, setCountryOptions] = useState<IOptions[]>([]);
  const [bankCountry, setBankCountry] = useState('');
  const [currencyOptions, setCurrencyOptions] = useState<IOptions[]>([]);
  const [bankCurrency, setBankCurrency] = useState('');
  const [bankData, setBankData] = useState();
  const { selectedAccount } = useAppSelector(state => state.auth);

  // Schema to validate account form.
  const schema = yup.object().shape({
    bankName: yup.string().required("Bank name is required."),
    branchName: yup.string().required("Branch name is required."),
    country: yup.string().required("Country is required."),
    IFSC: yup.string().required("IFSC is required."),
    currency: yup.string().required("Currency is required."),
    accountNumber: yup.string().required("Account number is required."),
    accountName: yup.string().required("Account name is required.")
  });

  //form
  const methods = useForm<IBankFormInputs>({ mode: 'onTouched', resolver: yupResolver(schema) });
  const { status } = useAppSelector(state => state.banks);

  useEffect(() => {
    const getBankDetails = async () => {
      if (bankId) {
        const bd: any = await dispatch(fetchBankById(bankId));

        setBankData(bd.payload);

        methods.setValue('accountName', bd.payload.accountName);
        methods.setValue('accountNumber', bd.payload.accountNumber);
        methods.setValue('bankName', bd.payload.bankName);
        methods.setValue('branchName', bd.payload.branchName);
        methods.setValue('country', bd.payload.country?._id);
        methods.setValue('IFSC', bd.payload.IFSC);
        methods.setValue('currency', bd.payload.currency._id);
        methods.setValue('SWIFT', bd.payload.SWIFT);
        methods.setValue('MICR', bd.payload.MICR);
        methods.setValue('IBAN', bd.payload.IBAN);
        setBankCountry(bd.payload.country?._id);
        setBankCurrency(bd.payload.currency?._id);
      }
    }

    getBankDetails();
  }, [bankId, dispatch, methods, selectedAccount]);

  useEffect(() => {
    const getCurrencies = async () => {
      if (!currencyOptions.length) {
        const data: any = await dispatch(fetchCurrencies());

        const options = data.payload.map((c: any) => {
          return { label: c.code, value: c._id }
        });
        setCurrencyOptions(options);

        // Auto-select if only one option
        if (options.length === 1 && !bankId) {
          setBankCurrency(options[0].value);
          methods.setValue('currency', options[0].value, { shouldValidate: true });
        }
      }
    }

    if (open) {
      getCurrencies();
    }
  }, [open, currencyOptions, dispatch, bankId, methods]);

  useEffect(() => {
    const getCountries = async () => {
      if (!countryOptions.length) {
        const data: any = await dispatch(fetchCountries());

        const options = data.payload.map((c: any) => {
          return { label: c.name, value: c._id }
        });
        setCountryOptions(options);

        // Auto-select if only one option
        if (options.length === 1 && !bankId) {
          setBankCountry(options[0].value);
          methods.setValue('country', options[0].value, { shouldValidate: true });
        }
      }
    }

    if (open) {
      getCountries();
    }

  }, [open, dispatch, countryOptions, bankId, methods]);

  const updateBankDetails = async (e: any) => {
    if (bankId) {
      const resData: any = await dispatch(updateBank({
        bankPayload: e,
        id: bankId
      }));

      if (!resData.error) {
        onClose(true);
      }
    }
  }

  const createBankDetails = async (e: any) => {
    const resData: any = await dispatch(addBank({ ...e, ...{ account: params.accountId } }));

    if (!resData.error) {
      onClose(true);
    }
  }

  const onSubmit = (e: any) => {
    if (bankId) {
      updateBankDetails(e);
      return;
    }

    createBankDetails(e);
  }

  const handleCancel = () => {
    methods.reset();
    onClose(false);
  }

  return (
    <Modal
      open={open}
      onClose={() => onClose(false)}
    >
      <FormProvider {...methods}>
        {(!bankId || (bankId && bankData)) &&
          <Box sx={style} component="form" onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            <Grid container >
              <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p: 2, pl: 2, bgcolor: '#e9ecf1' }} style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }} >
                <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
                  <LoadingButton
                    type="submit"
                    name={'submit'}
                    // disabled={!methods.formState.isValid}
                    loading={status === ApiStatus.LOADING}
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                    variant="contained"
                    size='small'>
                    {bankId ? 'Save' : 'Create'}
                  </LoadingButton>

                  <Button
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                    variant="contained"
                    size='small'
                    onClick={handleCancel} >
                    Cancel
                  </Button>
                </div>
                <h3 style={{ marginTop: -2 }}>{bankId ? 'Bank Details Update' : 'Add Bank Details'}</h3>
              </Grid>

            </Grid>


        <Grid container p={2} spacing={2}>
          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Account Name*</h5>
            <TextField
              placeholder='Account Name'
              {...methods.register('accountName')}
              required
              size='small'
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
              }} />
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.accountName?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Account Number*</h5>
            <TextField
              placeholder='Account Number'
              {...methods.register('accountNumber')}
              required
              size='small'
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
              }} />
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.accountNumber?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Bank Name*</h5>
            <TextField
              placeholder='Bank Name'
              {...methods.register('bankName')}
              required
              size='small'
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
              }} />
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.bankName?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Branch Name*</h5>
            <TextField
              placeholder='Branch Name'
              {...methods.register('branchName')}
              required
              size='small'
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
              }} />
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.branchName?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Country*</h5>
            {countryOptions?.length &&
              <Select
                className='c-text-field-4'
                {...methods.register('country')}
                disableUnderline
                required
                MenuProps={{
                  PaperProps: {
                    sx: {
                      border: "1.5px solid #e9ecf1",
                      boxShadow: 'none',
                      borderRadius: '5px'
                    }
                  }
                }}
                variant='outlined'
                sx={{
                  width: '100%',
                  boxShadow: "none",
                  paddingTop: '0px !important',
                  borderRadius: '7px !important',
                  marginTop: '4px',
                  '& .MuiOutlinedInput-input': { p: 0.7 },
                  marginBottom: '3px',
                  ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                }}
                value={bankCountry ? bankCountry : ''}
                onChange={(event: SelectChangeEvent<any>) => {
                  methods.setValue("country", event.target.value, { shouldValidate: true });
                  setBankCountry(event.target.value);
                }}
              >
                {countryOptions && countryOptions.map((option) =>
                  <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                )}
              </Select>}
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.country?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Currency*</h5>
            {currencyOptions?.length &&
              <Select
                className='c-text-field-4'
                {...methods.register('currency')}
                disableUnderline
                required
                MenuProps={{
                  PaperProps: {
                    sx: {
                      border: "1.5px solid #e9ecf1",
                      boxShadow: 'none',
                      borderRadius: '5px'
                    }
                  }
                }}
                variant='outlined'
                sx={{
                  width: '100%',
                  boxShadow: "none",
                  paddingTop: '0px !important',
                  borderRadius: '7px !important',
                  marginTop: '4px',
                  '& .MuiOutlinedInput-input': { p: 0.7 },
                  marginBottom: '3px',
                  ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                }}
                value={bankCurrency ? bankCurrency : ''}
                onChange={(event: SelectChangeEvent<any>) => {
                  methods.setValue("currency", event.target.value, { shouldValidate: true });
                  setBankCurrency(event.target.value);
                }}
              >
                {currencyOptions && currencyOptions.map((option) =>
                  <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                )}
              </Select>}
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.currency?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>IFSC*</h5>
            <TextField
              placeholder='IFSC Code'
              {...methods.register('IFSC')}
              required
              size='small'
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
              }} />
            <p style={{ color: "#c33b3b" }}>{methods.formState.errors.IFSC?.message ?? ""}</p>
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>SWIFT Code</h5>
            <TextField
              placeholder='SWIFT Code'
              {...methods.register('SWIFT')}
              size='small'
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
              }} />
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>MICR Code</h5>
            <TextField
              placeholder='MICR Code'
              {...methods.register('MICR')}
              size='small'
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
              }} />
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>IBAN</h5>
            <TextField
              placeholder='IBAN'
              {...methods.register('IBAN')}
              size='small'
              className='c-text-field-4'
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              InputProps={{
                disableUnderline: true,
              }} />
          </Grid>
        </Grid>
          </Box>
        }
      </FormProvider>
    </Modal>
  );
};

export default DialogBankForm;